import type { ApiClient } from '@carbonwebmail/api-client'
import { useCallback } from 'react'
import { when } from '../../ui/format.js'
import { EmptyCard, PagedScreen } from '../../ui/PagedScreen.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Td, Tr } from '../../ui/Table.js'
import { usePaged } from '../../ui/usePaged.js'
import { StuckMessages } from './StuckMessages.js'
import { SEND_STATUS } from './statusLabels.js'

/**
 * Every send attempt and what came back.
 *
 * The reason column is why this exists. For three days the only way to learn
 * why an email had not arrived was to open Postgres and read `send_log.reason`
 * by hand — the logs said `status: rejected` and stopped there. A provider's
 * own sentence ("your From address has not been confirmed as a Sender
 * Signature") is the difference between knowing and guessing.
 */
export function SendsPage({ client }: { readonly client: ApiClient }) {
  const load = useCallback((page: number) => client.sends(page), [client])
  const { state, setPage, retry } = usePaged(
    load,
    'We could not load your sending monitor. Please try again in a moment.',
  )

  return (
    <PagedScreen
      title="Sending monitor"
      intro="What is stuck, then every attempt with what the provider said about it."
      before={
        <>
          <StuckMessages client={client} status="unknown" />
          <StuckMessages client={client} status="dead" />
          <h2 className="mb-3 font-bold text-[15px] text-t1">Every attempt</h2>
        </>
      }
      state={state}
      onRetry={() => void retry()}
      onPage={setPage}
      head={['When', 'Status', 'Provider', 'Reason']}
      minWidth={720}
      empty={
        <EmptyCard heading="Nothing has been sent yet">
          Every attempt will appear here the moment a worker makes it, whether it was accepted,
          refused, or came back without an answer.
        </EmptyCard>
      }
      row={(item) => (
        <Tr key={item.messageId}>
          <Td>{when(item.createdAt)}</Td>
          <Td>
            <StatusPill tone={SEND_STATUS[item.status].tone}>
              {SEND_STATUS[item.status].label}
            </StatusPill>
          </Td>
          <Td>{item.provider}</Td>
          {/*
            Shown in full rather than truncated. It is one sentence and the only
            actionable thing on the row; hiding it behind a hover would put the
            answer where nobody looks.
          */}
          <Td>{item.reason ?? '—'}</Td>
        </Tr>
      )}
    />
  )
}
